// src/components/AddPermanentGuestModalForList.jsx – via & name required with conditional asterisks
import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";

function AddPermanentGuestModalForList({ onClose, onSave, currentUserName }) {
  const [name, setName] = useState("");
  const [via, setVia] = useState(currentUserName || "");
  const [admissionType, setAdmissionType] = useState("FREE");
  const [customDiscount, setCustomDiscount] = useState(50);
  const [notes, setNotes] = useState("");
  const [plus, setPlus] = useState(0);
  const [category, setCategory] = useState("REGULAR");

  const nameInputRef = useRef(null);
  const nameRequired = name.trim() === "";
  const viaRequired = via.trim() === "";

  const handleSave = useCallback(() => {
    if (nameRequired || viaRequired) return;
    onSave({
      name: name.trim(),
      plus,
      via: via.trim(),
      admission_type: admissionType === "DISCOUNT" ? `${customDiscount}%` : "FREE",
      notes,
      category,
    });
  }, [name, plus, via, admissionType, customDiscount, notes, category, onSave, nameRequired, viaRequired]);

  const handleClose = useCallback(() => {
    document.activeElement.blur();
    onClose();
  }, [onClose]);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  useEffect(() => {
    setVia(currentUserName || "");
  }, [currentUserName]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (!nameRequired && !viaRequired) handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSave, handleClose, nameRequired, viaRequired]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-4 w-[350px] relative">
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h3 className="text-lg font-bold mb-3">Add Permanent Guest</h3>

        {/* Name */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Names{nameRequired && <span className="text-red-600">*</span>}
          </label>
          <input
            type="text"
            ref={nameInputRef}
            className="w-full border rounded-md p-1.5 text-sm focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter guest name, e.g., John Doe"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {/* Via */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Via{viaRequired && <span className="text-red-600">*</span>}
          </label>
          <input
            className="w-full border rounded-md p-1.5 text-sm focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter source, e.g., Admin"
            value={via}
            onChange={(e) => setVia(e.target.value)}
          />
        </div>

        {/* Category */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            className="w-full border rounded-md p-1.5 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="REGULAR">Regular</option>
            <option value="VIP">VIP</option>
            <option value="BACKSTAGE">Backstage</option>
            <option value="ARTIST">Artist</option>
            <option value="STAFF">Staff</option>
        </select>
        </div>

        {/* Admission & Plus */}
        <div className="mb-3">
          <div className="flex space-x-4 items-start">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Admission</label>
              <div className="flex items-center space-x-3 text-sm">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="admission"
                    value="FREE"
                    checked={admissionType === "FREE"}
                    onChange={() => setAdmissionType("FREE")}
                    className="mr-1 h-3.5 w-3.5"
                  />
                  Free
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="admission"
                    value="DISCOUNT"
                    checked={admissionType === "DISCOUNT"}
                    onChange={() => setAdmissionType("DISCOUNT")}
                    className="mr-1 h-3.5 w-3.5"
                  />
                  <span className="flex items-center">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={customDiscount}
                      onChange={(e) => setCustomDiscount(Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
                      onFocus={(e) => e.target.select()}
                      className="w-12 border rounded p-1 text-center"
                      disabled={admissionType !== "DISCOUNT"}
                    />
                    %
                  </span>
                </label>
              </div>
            </div>
            <div className="ml-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Plus</label>
              <input
                type="number"
                min={0}
                className="w-16 border rounded-md p-1 text-center text-sm"
                value={plus}
                onChange={(e) => setPlus(Math.max(0, Number(e.target.value) || 0))}
                onFocus={(e) => e.target.select()}
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            className="w-full border rounded-md p-1.5 text-sm"
            rows={2}
            placeholder="Additional notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-center space-x-4 mt-4">
          <button
            className="px-5 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40"
            onClick={handleSave}
            disabled={nameRequired || viaRequired}
          >
            Save
          </button>
          <button
            className="px-5 py-1.5 rounded-md bg-white text-gray-700 text-sm font-medium border hover:bg-gray-100"
            onClick={handleClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

AddPermanentGuestModalForList.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentUserName: PropTypes.string,
};

export default AddPermanentGuestModalForList;
